import * as React from 'react';
import { FormEvent, InputHTMLAttributes, KeyboardEvent } from 'react';
import styled from 'styled-components';

import { BREAK_POINTS, COLOURS } from '../../consts/style';
import { ButtonAction } from '../Button';
import { ContextHubConnector } from '../../services/contextHubConnector';
import { navToUrl } from '../../router/navigation';
import { Byline, Heading3 } from '../Text';
import { interaction } from '@origin-digital/reporting-client';

import { mediaProps, RippleLine } from '../SignUp/SignUp';
import { withMedia } from 'react-media-query-hoc';

const FindPlanContainer = styled.section`
  padding: 32px 20px;
  box-sizing: border-box;
  overflow: auto;
  max-width: 1140px;
  background: white;
  width: 100%;

  @media (min-width: ${BREAK_POINTS.MIN.XS}) {
    display: flex;
    padding: 32px 0px;
  }

  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    align-items: center;
    min-height: 320px;
  }
`;

const FindPlanInner = styled.div`
  display: inline-block;
  margin: 0 auto;
  @media (min-width: ${BREAK_POINTS.MIN.XS}) {
    max-width: 222px;
  }
  @media (min-width: ${BREAK_POINTS.MIN.SM}) {
    max-width: 300px;
  }
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    min-width: 294px;
  }
`;

const PostCodeLabel = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;

  @media (max-width: ${BREAK_POINTS.MIN.SM}) {
    display: none;
  }
`;

export const PostCodeInput = styled.input`
  display: block;
  border: none;
  border-bottom: 4px solid ${COLOURS.GREY.GREY_16};
  width: 4.3em;
  padding-left: 3px;
  height: 60px;
  box-sizing: border-box;
  padding: 0;

  font-size: 56px;
  letter-spacing: 24px;

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  &:focus {
    outline: none;
    color: black;
  }

  ::placeholder {
    color: ${COLOURS.GREY.GREY_16};
    font-weight: 500;
  }

  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    font-size: 56px;
    height: 66px;
    box-sizing: border-box;
    padding-right: 1px;
    padding-top: 1px;
    padding-bottom: 1px;
  }
` as any;

type TargetEvent = FormEvent<InputHTMLAttributes<HTMLInputElement>>;

interface FindPlanPureProps {
  headline: string;
  byline: string;
  buttonText: string;
  buttonUrl: string;
  postCodeValue: string;
  topline: string;
  onChange?: (e: any) => void;
  onKeyPress?: (e: any) => void;
  onSubmit?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  media: mediaProps;
  active: boolean;
}

const tracking = (trackingId, trackingLabel) => {
  interaction('click', {
    componentType: 'button', //Required
    id: trackingId, //Required; second component of data-id
    parentContainer: 'homepage', //Required; first component of data-id
    appName: 'homepage', //Required
    label: trackingLabel, //Required; Text displayed on the button
  });
};

export const FindPlanPure = withMedia(
  ({
    headline,
    byline,
    buttonText,
    buttonUrl,
    postCodeValue,
    topline,
    onChange,
    onKeyPress,
    onSubmit,
    active,
    onFocus,
    onBlur,
  }: FindPlanPureProps) => (
    <FindPlanContainer data-comment="FindPlan">
      <FindPlanInner>
        <Heading3>{headline}</Heading3>
        <Byline>{byline}</Byline>
      </FindPlanInner>
      <FindPlanInner>
        {/*HACK for Andriod to show number keyboard, "number" doesnt work with (. -)*/}
        {/*3000 - default for now until we get location services*/}
        <PostCodeLabel>{topline}</PostCodeLabel>
        <PostCodeInput
          value={postCodeValue}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          inputmode="numeric"
          pattern="[0-9]*"
          title="Postal Code"
          placeholder={3000}
          type="tel"
        />
        <RippleLine active={active} />
        <ButtonAction href={buttonUrl} onClick={onSubmit!} tracking="findPlan">
          {buttonText}
        </ButtonAction>
      </FindPlanInner>
    </FindPlanContainer>
  ),
);

export class FindPlan extends React.Component<any, { postCodeValue: string; active: boolean }> {
  POSTCODE_LENGTH = 4;
  contextHub = new ContextHubConnector();

  constructor(props: any, state: any) {
    super(props, state);
    this.state = {
      postCodeValue: '',
      active: false,
    };
  }

  onKeyPress = (event: KeyboardEvent<HTMLFormElement>) => {
    // these are valid "number" ie '-2e10', but not valid for post codes
    if (event.key === 'Enter') {
      this.onSubmit();
    } else if (event.key.match(/[0-9]/) === null) {
      event.preventDefault();
    }
  };

  onFocus = () => {
    this.setState({ active: true });
  };
  onBlur = () => {
    this.setState({ active: false });
  };

  onChange = (event: TargetEvent) => {
    const value = (event.currentTarget.value as string).replace(/[^0-9]/gi, '');

    const postCodeValue =
      value.length > this.POSTCODE_LENGTH // do not let the field pass POSTCODE_LENGTH
        ? this.state.postCodeValue
        : value.length === 0
        ? ''
        : value;

    this.setState({ postCodeValue });

    if (value.length === this.POSTCODE_LENGTH) {
      this.onSubmit(postCodeValue);
    }
  };

  onSubmit = (postCodeValue?: string) => {
    const postcode = typeof postCodeValue === 'string' ? postCodeValue : this.state.postCodeValue;
    this.contextHub.setPostalCode(postcode);
    tracking(this.props.trackingId, this.props.trackingLabel);
    navToUrl(this.props.buttonUrl ? this.props.buttonUrl : this.props.buttonLink);
  };

  render() {
    const ClientComponent = this.props.component;
    return (
      <ClientComponent
        data-comment={this.props.dataComment}
        onKeyPress={this.onKeyPress}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onSubmit={this.onSubmit}
        {...this.state}
        {...this.props}
      />
    );
  }
}
